/** @module Specification/PartMaterialSize/Matrix */
define([
	"dojo/_base/declare",
	"underscore",
	"csi/constants",
	"csi/def/Heading",
	"csi/heading/Spec",
	"csi/html",
	"csi/table",
	"csi/table/matrix/Path",
	"csi/xml",
	"../SizeVariationParams",
], function(declare, _, constants, Heading, Spec, html, csiTable, Path, xml, {SIZE_PARAMS}) {

	const COLUMN_WIDTH_NAME = 75;

	var CSS_2ND_TD = csiTable.CSS_MATRIX_2ND_DIMENSION;
	var CSS_2ND_TH = constants.CSS_TABLE_COLUMNS + " " + CSS_2ND_TD;

	var _headingName = _.extend(new Heading(), {
		columnWidth: COLUMN_WIDTH_NAME,
		m_class: CSS_2ND_TD,
		m_prop: xml.PROP_NAME,
		m_right: xml.RIGHT_NONE
	});

	return declare(Path, {
		bAllowColumnFilter: true,
		columnPath: "Child:BOMProductSizes",
		restrictQuery: true,

		/** @property {string} id */
		id: SIZE_PARAMS.attr,

		/**
		 * @override
		 * Return specs for 1d/2d size matrix
		 */
		getColumnSpecs: function(ctx, h) {
			var specs = [];
			var sizeSet = ctx.view.bomActiveSizeSet;
			if (!sizeSet) {
				return specs;
			}

			// add header for 2nd dimension sizes
			if (sizeSet.has2dSizes()) {
				specs.push(new Spec({
					css: CSS_2ND_TH,
					heading: h,
					headerParams: {},	// want empty cell
					subs: [
						new Spec({
							css: CSS_2ND_TH,
							heading: _headingName,
							headerParams: {
								innerText: sizeSet.dim2Name
							}
						})
					]
				}));
			}

			var size1Store = sizeSet.getSize1Store();
			var sizes = size1Store.query();
			var sh = h.m_subheadings[0];
			var subs = [];

			ctx.forEach(sizes, (ctx, i) => {
				const params = {
					columnUrl: ctx.getNodeURL(),
					css: constants.CSS_TABLE_COLUMNS,
					heading: sh,
					headerParams: this._getColumnSpecHeaderParams(ctx, h, i)
				};

				// add extra styling if comparing with another view
				this.addColumnComparisonStyling(ctx, params);

				subs.push(new Spec(params));
			});

			var spec = new Spec({
				css: constants.CSS_TABLE_COLUMNS,
				heading: h,
				headerParams: {
					colSpan: subs.length,
					innerText: h.m_name
				},
				subs: subs
			});

			specs.push(spec);

			return specs;
		},

		/**
		 * @override
		 * Return key indicating size url(s) for column - no need to compare the 2d size header
		 */
		getCompareKeyForMergedCells(ctx, spec, index) {
			return (spec.heading === _headingName) ? "" : `${this.id}_${ctx.getNodeURL()}`;
		},

		/**
		 * @override
		 * Return true if column represents a new size
		 */
		isNewComparisonColumn(ctx, columnUrl) {
			return ctx.view.isNewSize(columnUrl);
		},

		/**
		 * @override
		 * Return true if column represents an old size
		 */
		isOldComparisonColumn(ctx, columnUrl) {
			return ctx.view.isOldSize(columnUrl);
		},

		/**
		 * @override
		 * Show size sub-header even if only one
		 */
		shouldRenderLoneSubheader: function(subSpec) {
			return true;
		},

		/**
		 * @override
		 * Return PartMaterial node. Actual nodes to be rendered will be returned in _getSpecNodes.
		 */
		_getRowNodes: function(ctx, qr, c, node) {
			return [node];
		},

		/**
		 * @override
		 * Return sizes appropriate for the current column
		 */
		_getSpecNodes: function(ctx, spec, nodes) {
			const {cache, view} = ctx;
			const {columnUrl} = spec;
			const sizeSet = view.getSizeSetForQueryResultInContext(ctx);

			if (!sizeSet) {
				return [];
			}

			// in 2d case, return the sizes that apply to current column - if no column url, then return all 2nd dimension sizes for the 2nd dimension header
			if (sizeSet.has2dSizes()) {
				// make sure first we allow Variations by Size
				const pm = ctx.getNodeOfType("PartMaterial");
				if (!pm || !pm.OKVariationByBOMSize) {
					return [];
				}
				return cache.getNodes(!columnUrl ? sizeSet.dim2SizeUrls : sizeSet.get2dSizeUrlsFor1stDimension(columnUrl));
			}

			// in 1d case, just return the column url representing the size
			return [cache.get(columnUrl)];
		},

		/**
		 * @override
		 * Add cell edit behavior
		 */
		_renderCellContent(ctx, h, td, columnSpec, index) {
			const {cache, queryResult, view} = ctx;
			const isDataCell = !!columnSpec.ra;
			let sizeUrl = ctx.getNodeURL();

			if (isDataCell) {
				const columnMap = view.getSizeColumnMap(ctx, queryResult, h.m_id);
				const variation = cache.get(columnMap[sizeUrl]);
				ctx.setNode(variation);
			}

			// size matrices are not editable inline
			ctx.setReadOnly(true);

			this.inherited(arguments);

			// only annotate cell with ability to edit if data rather than name of 2nd dimension size
			if (isDataCell) {
				if (ctx.getNode()) {
					ctx.view.postRenderSizeVariationCell(ctx, td, ctx.getNodeType());
				} else {
					// if no node in context, just add styling
					td.classList.add(html.CSS_NOT_APPLICABLE);
				}
			}

			// add styling if old size; add at the end or else it will be overridden by normal renderer
			if (view.isOldSize(sizeUrl)) {
				view.compareData.addOldStyling(td);
			} else if (view.isNewSize(sizeUrl)) {
				view.compareData.addNewStyling(td);
			}
		}
	});
});